import * as React from 'react';
import toast from 'react-hot-toast';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  ErrorState,
  FormField,
  Input,
  LoadingState,
  Page,
  PageHeader,
  PageSection,
  Select,
} from '@/components';
import { useTenantConfig, useUpdateTenantConfig } from '@/hooks';
import type { TenantConfig } from '@/types';

function clone<T>(v: T): T {
  return JSON.parse(JSON.stringify(v));
}

export function Settings() {
  const query = useTenantConfig();
  const update = useUpdateTenantConfig();

  const [draft, setDraft] = React.useState<TenantConfig | null>(null);

  React.useEffect(() => {
    if (query.data) setDraft(clone(query.data));
  }, [query.data]);

  const onSave = async () => {
    if (!draft) return;
    try {
      await update.mutateAsync(draft);
      toast.success('Configurações salvas');
      await query.refetch();
    } catch {
      toast.error('Não foi possível salvar');
    }
  };

  return (
    <Page>
      <PageHeader
        title="Configurações"
        description="Tenant, flags e políticas (sem secrets no frontend)."
        right={
          <div className="flex items-center" style={{ gap: 8 }}>
            <Button variant="secondary" onClick={() => query.refetch()} disabled={query.isFetching}>
              Recarregar
            </Button>
            <Button variant="primary" onClick={onSave} disabled={update.isLoading || !draft}>
              Salvar
            </Button>
          </div>
        }
      />

      <PageSection>
        {query.isLoading ? (
          <LoadingState title="Carregando configurações" />
        ) : query.isError ? (
          <ErrorState title="Falha ao carregar configurações" onRetry={() => query.refetch()} />
        ) : !draft ? null : (
          <div className="content-grid">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="font-semibold">Tenant</div>
                  <Badge variant="neutral">{draft.tenant_id}</Badge>
                </div>
              </CardHeader>
              <CardBody>
                <div className="flow-grid-2">
                  <FormField label="Nome">
                    <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
                  </FormField>
                  <FormField label="Status">
                    <Select
                      value={draft.status}
                      onChange={(e) => setDraft({ ...draft, status: e.target.value as any })}
                    >
                      <option value="active">active</option>
                      <option value="suspended">suspended</option>
                      <option value="maintenance">maintenance</option>
                    </Select>
                  </FormField>
                </div>
              </CardBody>
            </Card>

            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="font-semibold">Feature flags</div>
                  <Badge variant="neutral">MVP</Badge>
                </div>
              </CardHeader>
              <CardBody>
                <div className="flow-grid-2">
                  {(
                    [
                      ['inbound_enabled', 'Inbound (mensagens recebidas)'],
                      ['scheduled_enabled', 'Agendamentos'],
                      ['query_engine_enabled', 'Query Engine'],
                      ['analytics_enabled', 'Analytics'],
                    ] as const
                  ).map(([key, label]) => (
                    <label key={key} className="flow-chip" style={{ justifyContent: 'space-between' }}>
                      <span className="text-sm">{label}</span>
                      <input
                        type="checkbox"
                        checked={Boolean(draft.policies.features[key])}
                        onChange={(e) =>
                          setDraft({
                            ...draft,
                            policies: {
                              ...draft.policies,
                              features: { ...draft.policies.features, [key]: e.target.checked },
                            },
                          })
                        }
                      />
                    </label>
                  ))}
                </div>
              </CardBody>
            </Card>

            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="font-semibold">Rate limits</div>
                  <Badge variant="neutral">Políticas</Badge>
                </div>
              </CardHeader>
              <CardBody>
                <div className="flow-grid-2">
                  <FormField label="Mensagens/min">
                    <Input
                      type="number"
                      value={draft.policies.rate_limits.messages_per_minute}
                      onChange={(e) =>
                        setDraft({
                          ...draft,
                          policies: {
                            ...draft.policies,
                            rate_limits: {
                              ...draft.policies.rate_limits,
                              messages_per_minute: Number(e.target.value),
                            },
                          },
                        })
                      }
                    />
                  </FormField>
                  <FormField label="Mensagens/hora">
                    <Input
                      type="number"
                      value={draft.policies.rate_limits.messages_per_hour}
                      onChange={(e) =>
                        setDraft({
                          ...draft,
                          policies: {
                            ...draft.policies,
                            rate_limits: {
                              ...draft.policies.rate_limits,
                              messages_per_hour: Number(e.target.value),
                            },
                          },
                        })
                      }
                    />
                  </FormField>
                  <FormField label="Mensagens/dia">
                    <Input
                      type="number"
                      value={draft.policies.rate_limits.messages_per_day}
                      onChange={(e) =>
                        setDraft({
                          ...draft,
                          policies: {
                            ...draft.policies,
                            rate_limits: {
                              ...draft.policies.rate_limits,
                              messages_per_day: Number(e.target.value),
                            },
                          },
                        })
                      }
                    />
                  </FormField>
                  <FormField label="Timezone">
                    <Input
                      value={draft.policies.business_hours.timezone}
                      onChange={(e) =>
                        setDraft({
                          ...draft,
                          policies: {
                            ...draft.policies,
                            business_hours: { ...draft.policies.business_hours, timezone: e.target.value },
                          },
                        })
                      }
                    />
                  </FormField>
                </div>
              </CardBody>
            </Card>

            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="font-semibold">Canais / Data sources (read-only)</div>
                  <Badge variant="neutral">Sem tokens</Badge>
                </div>
              </CardHeader>
              <CardBody>
                <div className="flow-grid-2">
                  <FormField label="WhatsApp app_id">
                    <Input value={draft.whatsapp.app_id} disabled />
                  </FormField>
                  <FormField label="WhatsApp phone_number_id">
                    <Input value={draft.whatsapp.phone_number_id} disabled />
                  </FormField>
                  <FormField label="Contacts base_url">
                    <Input value={draft.data_sources.contacts.base_url} disabled />
                  </FormField>
                  <FormField label="Financial base_url">
                    <Input value={draft.data_sources.financial.base_url} disabled />
                  </FormField>
                </div>
              </CardBody>
            </Card>
          </div>
        )}
      </PageSection>
    </Page>
  );
}
